import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getEvidenceData, getReportMetadata, ReportMetadataRecord } from "@/features/reports/api/reportApi";
import aiClient from "@/shared/api/clients/aiClient";
import { VideoData, VideoStatus } from "../../../shared/interfaces/VideoData";
import { getTranscript } from "../../../shared/api/transcriptsApi";
import { useJobStore } from "../../../store/jobStore";
import {
  filterStalePlanInterrupts,
  findJobForThread,
  normalizePlanPresentation,
  resolvePlanInterrupts,
  resolvePlanThreadId,
  resolveThreadJobId,
  selectPlanInterrupt,
} from "../utils";
import { notifyPlanReady } from "@/features/hitl/notifyPlanReady";
import type { PlanReviewResponse, PlanReviewThread, ReviewEntity, ReviewGap } from "../types";

const REVISION_POLL_INTERVAL_MS = 2_000;
const REVISION_TIMEOUT_MS = 120_000;

export const formatEntityEditsForFeedback = (entities: ReviewEntity[]): string => {
  if (entities.length === 0) return "";
  const byCategory = new Map<string, ReviewEntity[]>();
  for (const entity of entities) {
    const list = byCategory.get(entity.category);
    if (list) list.push(entity);
    else byCategory.set(entity.category, [entity]);
  }
  const lines: string[] = [
    "Officer-revised entity outline (treat this as the desired final entity state):",
  ];
  for (const [category, list] of byCategory) {
    lines.push(`- ${category}:`);
    for (const entity of list) {
      const label = entity.label.trim() || "(untitled)";
      const detail = entity.details?.trim() ? ` — ${entity.details.trim()}` : "";
      const flag = entity.removed ? " [REMOVED]" : "";
      lines.push(`  - ${label}${detail}${flag}`);
    }
  }
  return lines.join("\n");
};

export const buildRevisionFeedback = (
  feedback: string,
  entities: ReviewEntity[],
  entitiesEdited: boolean,
  gaps: ReviewGap[]
): string => {
  const trimmedFeedback = feedback.trim();
  const clarifiedGaps = gaps.filter((gap) => gap.resolution?.trim());

  const sections: string[] = [];
  if (trimmedFeedback) {
    sections.push(trimmedFeedback);
  }

  // Always include the officer's revised entity outline when they edited
  // entities — the planner has no other channel to receive entity changes
  // (the present_plan tool only reads `approved` and `feedback` on
  // resume; structured `entityEdits` payloads were silently dropped).
  if (entitiesEdited) {
    const entityBlock = formatEntityEditsForFeedback(entities);
    if (entityBlock) sections.push(entityBlock);
  }

  if (clarifiedGaps.length > 0) {
    const clarificationLines = clarifiedGaps.map(
      (gap) => `- ${gap.description}: ${gap.resolution!.trim()}`
    );
    sections.push(`Officer clarifications:\n${clarificationLines.join("\n")}`);
  }

  if (sections.length === 0) return "";

  // Provide a leading instruction when the only signal is structured
  // (clarifications or entity edits), so the planner doesn't see a bare
  // outline with no directive.
  if (!trimmedFeedback) {
    sections.unshift("Please revise the outline using the officer's input below.");
  }

  return sections.join("\n\n");
};

/**
 * A 409 from `/receive`: the thread already moved past the interrupt we
 * answered. Distinct from every other submit failure because the payload can
 * never be accepted again — resyncing is the only recovery, never a retry.
 */
class PlanThreadAdvancedError extends Error {
  constructor(message = "Report is no longer waiting for input.") {
    super(message);
    this.name = "PlanThreadAdvancedError";
  }
}

/**
 * The agent API answers errors as text, not JSON. Recovers that body so the
 * server's message survives, the way the old `await response.text()` did.
 */
const agentErrorMessage = (err: unknown, fallback: string): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === "string" && data) return data;
    if (data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string") {
      return (data as { error: string }).error;
    }
  }
  return fallback;
};

export const usePlanReview = () => {
  const { reportID: routeThreadId } = useParams<{ reportID?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const markJobResumedAction = useJobStore((state) => state.markJobResumed);
  const markFullReportJobAction = useJobStore(
    (state) => state.markFullReportJob
  );

  const threadId = useMemo(
    () => resolvePlanThreadId(routeThreadId, searchParams, location.state),
    [location.state, routeThreadId, searchParams]
  );

  const [thread, setThread] = useState<PlanReviewThread | null>(null);
  const [reportMetadata, setReportMetadata] = useState<ReportMetadataRecord | null>(null);
  const [videoData, setVideoData] = useState<VideoData | VideoData[] | null>(null);
  const [feedback, setFeedback] = useState("");
  const [submittedInterruptId, setSubmittedInterruptId] = useState<string | null>(null);
  const [revisionStartedAt, setRevisionStartedAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [evidenceError, setEvidenceError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  // jobId carried on the thread's `configurable` — see
  // navigateToGenerationSurface for why this is a ref and not state.
  const threadJobIdRef = useRef<string | undefined>(undefined);

  const visibleInterrupts = useMemo(
    () => filterStalePlanInterrupts(thread, submittedInterruptId),
    [submittedInterruptId, thread]
  );
  const currentInterrupt = useMemo(
    () => selectPlanInterrupt(visibleInterrupts),
    [visibleInterrupts]
  );
  const currentPlan = useMemo(
    () => normalizePlanPresentation(currentInterrupt),
    [currentInterrupt]
  );
  const isAwaitingRevision = Boolean(submittedInterruptId);

  // ── Entity & gap state ─────────────────────────────
  const [entities, setEntities] = useState<ReviewEntity[]>([]);
  const [gaps, setGaps] = useState<ReviewGap[]>([]);
  // Tracks whether the officer has touched the entity outline since the
  // current plan was loaded. Used by buildRevisionFeedback to decide
  // whether to include the revised entity block in the feedback text on
  // Request Changes — without this flag, every revision would echo the
  // entity list back to the planner whether the officer edited it or not.
  const [entitiesEdited, setEntitiesEdited] = useState(false);

  // Sync from plan when a new plan arrives. Reset the edited flag —
  // any prior edits have already been transmitted (or discarded).
  useEffect(() => {
    if (currentPlan?.entities) setEntities(currentPlan.entities);
    else setEntities([]);
    if (currentPlan?.gaps) setGaps(currentPlan.gaps);
    else setGaps([]);
    setEntitiesEdited(false);
  }, [currentPlan]);

  const updateEntity = useCallback((id: string, patch: Partial<ReviewEntity>) => {
    setEntities((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    setEntitiesEdited(true);
  }, []);

  const removeEntity = useCallback((id: string) => {
    setEntities((prev) => prev.map((e) => (e.id === id ? { ...e, removed: true } : e)));
    setEntitiesEdited(true);
  }, []);

  const restoreEntity = useCallback((id: string) => {
    setEntities((prev) => prev.map((e) => (e.id === id ? { ...e, removed: false } : e)));
    setEntitiesEdited(true);
  }, []);

  const addEntity = useCallback((category: string) => {
    setEntities((prev) => [
      ...prev,
      { id: crypto.randomUUID(), category, label: "", details: "" },
    ]);
    setEntitiesEdited(true);
  }, []);

  const reorderEntities = useCallback((category: string, fromIndex: number, toIndex: number) => {
    setEntities((prev) => {
      const catItems = prev.filter((e) => e.category === category);
      const [moved] = catItems.splice(fromIndex, 1);
      catItems.splice(toIndex, 0, moved);
      // Rebuild in original category order
      const result: ReviewEntity[] = [];
      const catQueue = [...catItems];
      let catIdx = 0;
      for (const item of prev) {
        if (item.category === category) {
          result.push(catQueue[catIdx++]);
        } else {
          result.push(item);
        }
      }
      return result;
    });
    setEntitiesEdited(true);
  }, []);

  const resolveGap = useCallback((id: string, resolution: string) => {
    setGaps((prev) => prev.map((g) => (g.id === id ? { ...g, resolution } : g)));
  }, []);

  /**
   * The jobId behind this review, for every registry consumer below. The route
   * carries the reportId, and a registry entry only learns its reportId from a
   * /jobStatus snapshot — so the registry match alone misses, and the jobId the
   * thread advertises is the fallback.
   *
   * Read from a ref rather than `thread` state on purpose: refreshThread closes
   * over these callbacks and the mount effect re-runs whenever refreshThread's
   * identity changes, so depending on `thread` here would refetch the thread on
   * every update, forever.
   */
  const resolveJobId = useCallback(
    () =>
      findJobForThread(threadId, useJobStore.getState().jobs)?.id ??
      threadJobIdRef.current,
    [threadId],
  );

  const markJobResumed = useCallback(() => {
    const jobId = resolveJobId();
    if (!jobId) return;
    markJobResumedAction(jobId);
  }, [markJobResumedAction, resolveJobId]);

  const markFullReportJob = useCallback(() => {
    const jobId = resolveJobId();
    if (!jobId) return;
    markFullReportJobAction(jobId);
  }, [markFullReportJobAction, resolveJobId]);

  // After approval the job keeps generating — land on its generation surface,
  // not Home (the old second bounce). Falls back to Home only when neither the
  // registry nor the thread itself names the job.
  const navigateToGenerationSurface = useCallback(() => {
    const jobId = resolveJobId();
    navigate(jobId ? `/generating/${jobId}` : "/");
  }, [navigate, resolveJobId]);

  const refreshThread = useCallback(
    async ({
      silent = false,
      respondedInterruptId,
    }: {
      silent?: boolean;
      respondedInterruptId?: string | null;
    } = {}): Promise<PlanReviewThread | null> => {
      if (!threadId) return null;

      if (!silent) {
        setIsLoading(true);
      }
      setError("");

      try {
        let data: PlanReviewThread;
        try {
          ({ data } = await aiClient.post<PlanReviewThread>("/get-thread", {
            threadId,
          }));
        } catch (err) {
          throw new Error(
            agentErrorMessage(err, "Failed to load First Draft review."),
          );
        }

        setThread(data);
        threadJobIdRef.current = resolveThreadJobId(data) ?? threadJobIdRef.current;

        if (data.status === "FAILED") {
          setStatusMessage("");
          setError(typeof data.error === "string" ? data.error : "AI Assistant failed while waiting for input.");
          return data;
        }

        const nextVisibleInterrupts = filterStalePlanInterrupts(
          data,
          respondedInterruptId ?? submittedInterruptId
        );

        if (data.status === "RUNNING") {
          markJobResumed();
          setStatusMessage(
            submittedInterruptId
              ? "AI Assistant is revising the First Draft."
              : "AI Assistant resumed successfully."
          );
          return data;
        }

        if (data.status === "AWAIT" && nextVisibleInterrupts.length > 0) {
          if (respondedInterruptId ?? submittedInterruptId) {
            setSubmittedInterruptId(null);
            setRevisionStartedAt(null);
            setFeedback("");
            setStatusMessage("A revised First Draft is ready for review.");
            notifyPlanReady();
          } else {
            setStatusMessage("Review the proposed First Draft before report generation continues.");
          }
          return data;
        }

        if (data.status === "AWAIT" && nextVisibleInterrupts.length === 0) {
          const rawInterrupts = resolvePlanInterrupts(data);
          const activeRespondedInterruptId = respondedInterruptId ?? submittedInterruptId;

          if (
            activeRespondedInterruptId &&
            rawInterrupts.some(
              (interrupt) => interrupt.interruptId === activeRespondedInterruptId
            )
          ) {
            setStatusMessage("Waiting for the AI Assistant to pick up your feedback.");
            return data;
          }

          if (activeRespondedInterruptId) {
            setStatusMessage("AI Assistant continued without another approval step.");
            navigateToGenerationSurface();
            return data;
          }
        }

        setStatusMessage("");
        return data;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load First Draft review.");
        return null;
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [markJobResumed, navigateToGenerationSurface, submittedInterruptId, threadId]
  );

  const refreshMetadata = useCallback(async () => {
    if (!threadId) {
      setReportMetadata(null);
      setVideoData(null);
      setEvidenceError("");
      return;
    }

    setIsLoadingEvidence(true);
    setEvidenceError("");

    // Try report metadata first (available after report is generated)
    let nextMetadata: ReportMetadataRecord | null = null;
    try {
      nextMetadata = await getReportMetadata(threadId);
      setReportMetadata(nextMetadata);
    } catch (err) {
      // A 404 is expected during plan review — the report doesn't exist yet.
      // Any other failure (auth, network, server) is real: surface it rather
      // than silently presenting an empty evidence state as if there were none.
      setReportMetadata(null);
      if (!(isAxiosError(err) && err.response?.status === 404)) {
        setEvidenceError(
          err instanceof Error ? err.message : "Failed to load evidence."
        );
        setVideoData(null);
        setIsLoadingEvidence(false);
        return;
      }
    }

    // Resolve evidence IDs: prefer the job store when present, but fall back
    // to report metadata. The job store is in-memory and per-device, so it's
    // empty after a page reload or on a different device — in those cases the
    // metadata's evidenceIds is the only source, and without this fallback we
    // would skip the fetch entirely and wrongly show "no evidence attached".
    const job = useJobStore
      .getState()
      .jobs.find((j) => j.id === threadId || j.reportId === threadId);
    const evidenceIds =
      job?.evidenceIDs?.length ? job.evidenceIDs : nextMetadata?.evidenceIds ?? [];

    if (!evidenceIds.length) {
      setVideoData(null);
      setIsLoadingEvidence(false);
      return;
    }

    try {
      const evidenceResults = await Promise.allSettled(
        evidenceIds.map((id) => getEvidenceData(id))
      );
      const successfulEvidence = evidenceResults.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : []
      );
      const failedEvidenceCount = evidenceResults.length - successfulEvidence.length;

      // `/evidence/details` returns player metadata only — it never includes
      // the transcript body. Hydrate it here for already-transcribed videos so
      // VideoPreview's overlay + Edit/Download UI (gated on `transcript`)
      // render. Mirrors GenerateReport's useVideoData.
      await Promise.allSettled(
        successfulEvidence.map(async (video) => {
          if (video.status === VideoStatus.TRANSCRIPT_READY) {
            try {
              const { transcript } = await getTranscript(video.id);
              video.transcript = transcript;
            } catch {
              // Leave the transcript empty on failure; the overlay simply
              // won't render for this video and playback is unaffected.
            }
          }
        })
      );

      setVideoData(
        successfulEvidence.length === 0
          ? null
          : successfulEvidence.length === 1
            ? successfulEvidence[0]
            : successfulEvidence
      );

      if (failedEvidenceCount > 0) {
        setEvidenceError(
          successfulEvidence.length > 0
            ? "Some evidence previews could not be loaded."
            : "Evidence preview could not be loaded."
        );
      }
    } catch (err) {
      setVideoData(null);
      setEvidenceError(
        err instanceof Error ? err.message : "Failed to load evidence."
      );
    } finally {
      setIsLoadingEvidence(false);
    }
  }, [threadId]);

  useEffect(() => {
    void refreshThread();
    void refreshMetadata();
  }, [refreshMetadata, refreshThread]);

  useEffect(() => {
    if (!submittedInterruptId || !revisionStartedAt) return;

    const intervalId = window.setInterval(() => {
      if (Date.now() - revisionStartedAt >= REVISION_TIMEOUT_MS) {
        setSubmittedInterruptId(null);
        setRevisionStartedAt(null);
        setStatusMessage("First Draft revision is taking longer than expected. Refresh to try again.");
        return;
      }

      void refreshThread({ silent: true });
    }, REVISION_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [refreshThread, revisionStartedAt, submittedInterruptId]);

  const submitHitlResponse = useCallback(
    async (userInput: unknown) => {
      if (!threadId || !currentInterrupt) return null;

      try {
        const { data } = await aiClient.post<PlanReviewResponse>("/receive", {
          threadId,
          hitlResponses: [
            {
              interruptId: currentInterrupt.interruptId,
              userInput,
            },
          ],
        });
        return data;
      } catch (err) {
        // 409 means the thread moved on — its own copy, ahead of the body.
        if (isAxiosError(err) && err.response?.status === 409) {
          throw new PlanThreadAdvancedError();
        }
        // 502 FULL_REPORT_STATUS_UPDATE_FAILED: the server could not flip the
        // report off narrative-only, so it submitted nothing and started no
        // run. Its message already tells the officer to approve again, and the
        // thread still holds the interrupt, so the retry is this same call —
        // fall through to agentErrorMessage and surface it verbatim.
        throw new Error(
          agentErrorMessage(err, "Failed to submit First Draft review."),
        );
      }
    },
    [currentInterrupt, threadId]
  );

  /**
   * Resumes the plan interrupt with `approved: true`.
   *
   * `fullReport` sends `generateFullReport: true`, which makes the server chain
   * an extraction/assembly run onto the same job once the narrative is filed —
   * one jobId, two agent runs, no intermediate completion for the client to
   * handle. Narrative-only omits the field entirely (the server rejects a
   * non-boolean, and absent is its documented "no").
   *
   * Unique to this path: the approval itself can fail. Before returning 202 the
   * server synchronously flips the report off narrative-only, and a 502
   * FULL_REPORT_STATUS_UPDATE_FAILED means nothing was submitted and no run
   * started — the thread still holds its interrupt. So the officer stays here
   * with the server's message and can re-approve with the identical payload.
   */
  const submitApproval = useCallback(async ({ fullReport = false }: { fullReport?: boolean } = {}): Promise<boolean> => {
    if (!currentInterrupt) return false;

    setIsSubmitting(true);
    setError("");

    try {
      // Officer overrides on approval: typed comments, edited entity
      // outline, and resolved gap clarifications all ride into the
      // present_plan resume payload so the orchestrator (via
      // present-plan-body.ts officer_* XML sections) honors them when
      // delegating to extractors. The legacy `entityEdits` structured
      // field was dropped — backend never read it.
      const officerComments = feedback.trim() || undefined;
      const officerEntityOverrides = entitiesEdited
        ? formatEntityEditsForFeedback(entities)
        : undefined;
      const officerClarifications = gaps
        .filter((g) => g.resolution?.trim())
        .map((g) => ({
          description: g.description,
          resolution: g.resolution!.trim(),
        }));

      await submitHitlResponse({
        approved: true,
        ...(fullReport ? { generateFullReport: true } : {}),
        ...(officerComments ? { officerComments } : {}),
        ...(officerEntityOverrides ? { officerEntityOverrides } : {}),
        ...(officerClarifications.length > 0 ? { officerClarifications } : {}),
      });
      // Only past the 202: the run is committed. A full report is percentage-
      // tracked end to end, so drop the narrative-only countdown before the
      // generation surface reads the entry.
      if (fullReport) markFullReportJob();
      markJobResumed();
      navigateToGenerationSurface();
      return true;
    } catch (err) {
      // 409: the thread already moved past this interrupt (approved on another
      // device or tab), so re-approving can never land — every further pick in
      // the mode dialog would 409 again. Resync and follow the thread to
      // wherever the server took it instead of parking the officer on a dead
      // dialog. Resolving true lets the caller close it.
      if (err instanceof PlanThreadAdvancedError) {
        const data = await refreshThread({
          silent: true,
          respondedInterruptId: currentInterrupt.interruptId,
        });
        // The resync itself failed (network/server): we don't know where the
        // thread went, and refreshThread has already set its message. Keep the
        // dialog open so the officer reads it there and can retry, instead of
        // closing onto a card with the alert scrolled off the top.
        if (!data) return false;
        // refreshThread already navigates an AWAIT thread with no interrupt
        // left; a RUNNING one is generating with no approval step to come, so
        // follow it there too. Anything else keeps refreshThread's message and
        // the officer stays to read it.
        if (data.status === "RUNNING") navigateToGenerationSurface();
        return true;
      }
      setError(err instanceof Error ? err.message : "Failed to approve First Draft.");
      // Nothing was submitted (502) and the interrupt is still live, so the
      // caller keeps the officer on the dialog to approve again.
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentInterrupt,
    entities,
    entitiesEdited,
    feedback,
    gaps,
    markFullReportJob,
    markJobResumed,
    navigateToGenerationSurface,
    refreshThread,
    submitHitlResponse,
  ]);

  const submitFeedback = useCallback(async () => {
    if (!currentInterrupt) return;
    const revisionFeedback = buildRevisionFeedback(
      feedback,
      entities,
      entitiesEdited,
      gaps
    );

    if (!revisionFeedback.trim()) {
      setError("Enter feedback before requesting changes.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await submitHitlResponse({
        approved: false,
        feedback: revisionFeedback,
      });
      markJobResumed();
      setSubmittedInterruptId(currentInterrupt.interruptId);
      setRevisionStartedAt(Date.now());
      setStatusMessage("AI Assistant is revising the First Draft.");
      await refreshThread({
        silent: true,
        respondedInterruptId: currentInterrupt.interruptId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit First Draft changes.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentInterrupt,
    entities,
    entitiesEdited,
    feedback,
    gaps,
    markJobResumed,
    refreshThread,
    submitHitlResponse,
  ]);

  const handleReturnHome = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // A failed approval leaves its message on screen after the dialog closes.
  // Reopening the dialog is a fresh attempt, so the caller clears it first —
  // otherwise the officer picks a mode under the previous failure's alert.
  const clearError = useCallback(() => setError(""), []);

  const job = useJobStore
    .getState()
    .jobs.find((j) => j.id === threadId || j.reportId === threadId);

  return {
    threadId,
    thread,
    reportMetadata,
    videoData,
    videoTitles: job?.videoTitles,
    currentInterrupt,
    currentPlan,
    visibleInterrupts,
    feedback,
    setFeedback,
    isLoading,
    isLoadingEvidence,
    isSubmitting,
    isAwaitingRevision,
    error,
    clearError,
    evidenceError,
    statusMessage,
    submitApproval,
    submitFeedback,
    handleReturnHome,
    refreshThread,
    entities,
    entitiesEdited,
    gaps,
    updateEntity,
    removeEntity,
    restoreEntity,
    addEntity,
    reorderEntities,
    resolveGap,
    revisionStartedAt,
    revisionTimeoutMs: REVISION_TIMEOUT_MS,
  };
};
